import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue'

/**
 * Shared disclosure state machine for a streaming reasoning panel.
 *
 * The Creator "Avi" timeline and the shared roleplay/Open Worlds prose render
 * reasoning with very different looks but identical *behavior*:
 *
 * - While reasoning streams: the panel is expanded and pinned to its own bottom.
 * - When reasoning ends: it collapses to a small chip (unless the host seeds it
 *   open via `autoScrollCancelled`).
 * - Once completed, the panel is always freely collapsible/expandable, however
 *   it was first opened. `manualExpanded` is the single source of truth; the
 *   `autoScrollCancelled` seed only supplies the default before the user acts.
 *
 * The "reasoning is live" signal differs per surface, so it is *injected*: the
 * Creator passes `status === 'reasoning'`; the shared prose path passes
 * `isStreaming && !contentStarted`. Everything else is shared.
 */
interface ReasoningDisclosureOptions {
  liveReasoning: () => string | null | undefined
  savedReasoning: () => string | null | undefined
  /** True while reasoning is actively streaming (host-specific signal). */
  reasoningActive: () => boolean
  /** Seeds the completed panel open (e.g. the user was reading mid-stream). */
  autoScrollCancelled?: () => boolean
}

const SCROLL_KEYS = new Set(['PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End', ' ', 'Spacebar'])

export function useReasoningDisclosure(options: ReasoningDisclosureOptions) {
  const autoScrollCancelled = options.autoScrollCancelled ?? (() => false)

  const scrollContainer = ref<HTMLElement | null>(null)
  const userScrolledUp = ref(false)
  let scrollFrame: number | null = null

  function cancelScroll() {
    if (scrollFrame !== null) cancelAnimationFrame(scrollFrame)
    scrollFrame = null
  }
  onBeforeUnmount(cancelScroll)
  // The user's explicit expand/collapse choice for completed reasoning.
  // `null` means "not yet chosen" — fall back to the seeded default.
  const manualExpanded = ref<boolean | null>(null)

  const liveReasoningText = computed(() => (options.liveReasoning()?.trim() ? options.liveReasoning()! : ''))
  const savedReasoningText = computed(() => (options.savedReasoning()?.trim() ? options.savedReasoning()! : ''))
  const displayReasoning = computed(() => liveReasoningText.value || savedReasoningText.value)
  const hasLiveReasoning = computed(() => Boolean(liveReasoningText.value) && options.reasoningActive())
  // Reasoning has finished streaming but we still have text to show.
  const reasoningComplete = computed(() => Boolean(displayReasoning.value) && !hasLiveReasoning.value)
  const completedExpanded = computed(() => manualExpanded.value ?? autoScrollCancelled())
  const showReasoningPanel = computed(
    () => hasLiveReasoning.value || (reasoningComplete.value && completedExpanded.value),
  )
  const showCollapsedReasoning = computed(() => reasoningComplete.value && !completedExpanded.value)
  // The completed reasoning panel is always collapsible, regardless of how it opened.
  const canCollapseReasoningPanel = computed(() => reasoningComplete.value)

  function onWheel() { userScrolledUp.value = true }
  function onTouchMove() { userScrolledUp.value = true }
  function onKeyDown(e: KeyboardEvent) { if (SCROLL_KEYS.has(e.key)) userScrolledUp.value = true }
  function onScroll() {
    const el = scrollContainer.value
    if (!el) return
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) userScrolledUp.value = false
  }

  watchEffect((onCleanup) => {
    const el = scrollContainer.value
    if (!el) return
    el.addEventListener('wheel', onWheel, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: true })
    el.addEventListener('keydown', onKeyDown)
    el.addEventListener('scroll', onScroll, { passive: true })
    onCleanup(() => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('keydown', onKeyDown)
      el.removeEventListener('scroll', onScroll)
    })
  })

  // Coalesce layout reads after DOM updates. Check user intent again at execution
  // time so input between scheduling and painting never pulls the reader down.
  watch(
    [displayReasoning, showReasoningPanel],
    () => {
      if (!showReasoningPanel.value) { cancelScroll(); return }
      if (!hasLiveReasoning.value || userScrolledUp.value || scrollFrame !== null) return
      scrollFrame = requestAnimationFrame(() => {
        scrollFrame = null
        const el = scrollContainer.value
        if (el && showReasoningPanel.value && !userScrolledUp.value) el.scrollTop = el.scrollHeight
      })
    },
    { flush: 'post' },
  )

  // A fresh reasoning stream resets any prior manual choice so the next
  // completion re-derives its default from the seed.
  watch(
    () => hasLiveReasoning.value,
    (isLive) => {
      if (isLive) manualExpanded.value = null
    },
  )

  function expandReasoningPanel() {
    manualExpanded.value = true
  }

  function collapseReasoningPanel() {
    if (!canCollapseReasoningPanel.value) return
    manualExpanded.value = false
  }

  return {
    scrollContainer,
    liveReasoningText,
    savedReasoningText,
    displayReasoning,
    hasLiveReasoning,
    reasoningComplete,
    showReasoningPanel,
    showCollapsedReasoning,
    canCollapseReasoningPanel,
    expandReasoningPanel,
    collapseReasoningPanel,
  }
}
